const SUCCESS_RESULT_CODES: string[] = ['0', '10002', '200', '206'];
const UNSUPPORTED_DEVICE_RESULT_CODE = '202';

export class BreadPushProvisioningSDK {
  google?: GoogleOptions;
  apple?: AppleOptions;

  constructor(options: SdkOptions) {
    if (options.google) {
      this.google = options.google;
      if ('button' in this.google) {
        this.google.button.addEventListener('click', e =>
          this.handleGoogleOnClick(e),
        );
      }
    }
    if (options.apple) {
      this.apple = options.apple;

      if (!this.apple.button.id) {
        throw new Error('Apple button must have an id');
      }

      if (this.apple.onPushProvisionStart) {
        this.apple.button.addEventListener('click', () => {
          // has to check again because in the callback TS doesn't know
          // if these values are still defined.
          if (this.apple && this.apple.onPushProvisionStart) {
            this.apple.onPushProvisionStart &&
              this.apple.onPushProvisionStart();
          }
        });
      }

      window.initAddToAppleWallet({
        partnerId: this.apple.partnerId,
        domain: 'https://apple-pay.apple.com',
        buttonId: this.apple.button.id,
        jwtResolver: async () => await this.handleApplePushProvision(),
        resultResolver: (result: AppleResultDetails) =>
          this.handleAppleResult(result),
      });
    }
  }

  private handleGoogleOnClick(e: MouseEvent) {
    if (this.google) {
      e.preventDefault();
      if (this.google.onPushProvisionStart) {
        this.google.onPushProvisionStart();
      }
      window.googlepay.openAppWindow({
        integratorId: this.google.integratorId,
        isTestEnvironment: this.google.isTestEnvironment,
        tokenSetting: this.google.tokenSetting,
        cardSetting: this.google.cardSetting,
        onSessionCreated: async (payload: GooglePayload) =>
          await this.handleGooglePushProvision(payload),
        onSuccess: () => this.handleGoogleResult('success'),
        onFailure: (details: GoogleResultDetails) =>
          this.handleGoogleResult('failure', details),
        onCancel: () => this.handleGoogleResult('cancel'),
      });
    }
  }

  private async handleApplePushProvision() {
    if (this.apple) {
      this.apple.onPushProvision();
    }
  }

  private handleAppleResult(result: AppleResultDetails) {
    if (!this.apple) {
      return;
    }

    if (this.apple) {
      if (UNSUPPORTED_DEVICE_RESULT_CODE === result.status) {
        this.apple.onResult('unsupportedDevice');
      } else if (SUCCESS_RESULT_CODES.indexOf(result.status) >= 0) {
        this.apple.onResult('success');
      }
    }
  }

  private async handleGooglePushProvision(payload: GooglePayload) {
    if (!this.google) {
      return;
    }

    if (payload.tokenSetting === 0) {
      this.google.onResult('unsupportedDevice');
      window.googlepay.closeAppWindow();
    } else {
      try {
        this.google.onPushProvision(payload);
      } catch (err) {
        window.googlepay.closeAppWindow();
        throw err;
      }
    }
  }

  private handleGoogleResult(
    result: PushProvisionResult,
    details?: GoogleResultDetails,
  ) {
    if (this.google && 'button' in this.google) {
      this.google.onResult(result, details);
    }
  }

  /**
   * This method is provided so that the user of the SDK has the option of
   * handling the onClick event themselves.
   */
  googlePushProvision(options: {
    onPushProvision: (payload: GooglePayload) => unknown;
    onResult: (result: PushProvisionResult) => unknown;
  }) {
    if (this.google) {
      this.google.onPushProvision = options.onPushProvision;
      this.google.onResult = options.onResult;
    }
  }
}

interface GoogleBaseArgs {
  integratorId: string;
  tokenSetting: number;
  cardSetting: number;
  isTestEnvironment: boolean;
  hl: string;
  onPushProvisionStart: () => void;
  onPushProvision: (payload: GooglePayload) => unknown;
  onResult: (
    result: PushProvisionResult,
    details?: GoogleResultDetails,
  ) => unknown;
}

export type GoogleWithoutButton = GoogleBaseArgs;

export interface GoogleWithButton extends GoogleBaseArgs {
  button: HTMLButtonElement;
}

export type GoogleOptions = GoogleWithoutButton | GoogleWithButton;

export type AppleOptions = {
  partnerId: string;
  button: HTMLButtonElement;
  onPushProvision: () => unknown;
  onResult: (
    result: PushProvisionResult,
    details?: AppleResultDetails,
  ) => unknown;
  onPushProvisionStart?: () => void;
};

export interface AppleInitParams {
  partnerId: string;
  domain: string;
  buttonId: string;
  jwtResolver: () => unknown;
  resultResolver: (result: AppleResultDetails) => unknown;
}

export interface GoogleOpenParams {
  integratorId: string;
  isTestEnvironment: boolean;
  tokenSetting: number;
  cardSetting: number;
  onSessionCreated: (payload: GooglePayload) => unknown;
  onSuccess: () => unknown;
  onFailure: (details: GoogleResultDetails) => unknown;
  onCancel: () => unknown;
}

/**
 * Assume google pay and apple pay SDK has been loaded.
 */
declare global {
  interface Window {
    googlepay: {
      openAppWindow: (google: GoogleOpenParams) => void;
      closeAppWindow: () => void;
    };
    initAddToAppleWallet: (apple: AppleInitParams) => void;
  }
}

export type PushProvisionResult =
  | 'success'
  | 'failure'
  | 'unsupportedDevice'
  | 'cancel';

export interface GooglePayload {
  serverSessionId: string;
  tokenSetting: number;
  cardSetting: number;
  publicDeviceId: string;
  publicWalletId: string;
  clientSessionId: string;
}

export interface GooglePushError {
  errorCode: string;
}

export interface GoogleResultDetails {
  tokenResult?: string;
  cardResult?: string;
  debugInfo?: string;
  errors: GooglePushError[];
}

export interface AppleResultDetails {
  status: string;
}

export interface SdkOptions {
  google?: GoogleOptions;
  apple?: AppleOptions;
}

export default BreadPushProvisioningSDK;
